import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Sibling AJAX Navigation
 *
 * Intercepts clicks on sibling product links (flavor/format) and performs
 * AJAX navigation by fetching the target product's AJAX template and
 * morphing #MainContent in place.
 *
 * Uses event delegation — no custom elements needed. Links are standard
 * <a> elements enhanced with data-sibling-link and data-ajax-url attributes.
 */

/** @type {Set<string>} URLs already scheduled for prefetch (avoids duplicate requests) */
const prefetched = new Set();

// ---------------------------------------------------------------------------
// Accessibility — live region for screen reader announcements
// ---------------------------------------------------------------------------

/** @returns {HTMLElement} */
const getOrCreateLiveRegion = () => {
  let el = document.getElementById('sibling-nav-status');
  if (!el) {
    el = document.createElement('div');
    el.id = 'sibling-nav-status';
    el.className = 'visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  return el;
};

/**
 * Announces a message to screen readers via the live region.
 * @param {string} message
 */
const announce = (message) => {
  const el = getOrCreateLiveRegion();
  el.textContent = '';
  requestAnimationFrame(() => {
    el.textContent = message;
  });
};

/** @type {AbortController|null} Active navigation request controller */
let activeController = null;

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

/**
 * Fetches a page's HTML. Relies on the browser HTTP cache for deduplication
 * — no in-memory cache needed.
 * @param {string} ajaxUrl - The AJAX template URL to fetch
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<string>} The fetched HTML string
 */
const fetchPage = async (ajaxUrl, signal) => {
  const response = await fetch(ajaxUrl, { signal });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.text();
};

/**
 * Prefetches a sibling page in the background with low priority.
 * The response is not stored — the browser HTTP cache handles deduplication
 * and respects server cache headers for freshness.
 * @param {string} ajaxUrl - The AJAX template URL to prefetch
 */
const prefetchSibling = (ajaxUrl) => {
  if (prefetched.has(ajaxUrl)) return;
  prefetched.add(ajaxUrl);

  fetch(ajaxUrl, { priority: 'low' }).catch(() => {
    // Silently ignore prefetch failures; allow retry
    prefetched.delete(ajaxUrl);
  });
};

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * Performs AJAX navigation to a sibling product
 * @param {string} displayUrl - The URL to show in the address bar
 * @param {string} ajaxUrl - The AJAX template URL to fetch
 * @param {string} [title] - The product title for the browser tab
 * @returns {Promise<void>}
 */
const navigateToSibling = async (displayUrl, ajaxUrl, title) => {
  // Cancel any in-flight navigation
  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;

  // Loading state on body only — morphed content may replace the link
  document.body.classList.add('sibling-loading');
  const loadingText = document.querySelector('[data-loading-text]')?.dataset.loadingText || 'Loading product…';
  announce(loadingText);

  try {
    const html = await fetchPage(ajaxUrl, controller.signal);

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newContent = doc.querySelector('#MainContent') || doc.body;
    const currentContent = document.querySelector('#MainContent');

    if (!currentContent) {
      throw new Error('Could not find #MainContent in current page');
    }

    const scrollY = window.scrollY;
    document.documentElement.style.scrollBehavior = 'auto';
    morph(currentContent, newContent, MORPH_OPTIONS);
    window.scrollTo(0, scrollY);
    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = '';
    });

    // Update page title
    if (title) {
      document.title = title;
    }

    // Update browser history
    history.pushState({ siblingNav: true, ajaxUrl, title }, '', displayUrl);

    // Move focus to main content for screen readers
    const mainContent = document.querySelector('#MainContent');
    if (mainContent instanceof HTMLElement) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus({ preventScroll: true });
    }

    // Announce the navigation to screen readers
    announce(title || document.title);

    // Dispatch navigated event
    window.dispatchEvent(
      new CustomEvent('sibling:navigated', {
        detail: { url: displayUrl },
      })
    );
  } finally {
    document.body.classList.remove('sibling-loading');
    // Only clear if no newer navigation has taken over
    if (activeController === controller) {
      activeController = null;
    }
  }
};

// ---------------------------------------------------------------------------
// Initial state — store current page so back button works
// ---------------------------------------------------------------------------

{
  // The current page's AJAX URL is computed server-side and emitted as a
  // data attribute on the sibling container element.
  const container = document.querySelector('[data-current-ajax-url]');

  if (container) {
    history.replaceState(
      { siblingNav: true, ajaxUrl: container.dataset.currentAjaxUrl, title: document.title },
      '',
      window.location.href
    );
  }
}

// ---------------------------------------------------------------------------
// Event delegation — click
// ---------------------------------------------------------------------------

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-sibling-link]');
  if (!link) return;

  // Allow modifier keys for normal navigation (new tab, etc.)
  if (event.metaKey || event.ctrlKey || event.shiftKey) return;

  // Skip current product links
  if (link.hasAttribute('aria-current')) return;

  const ajaxUrl = link.dataset.ajaxUrl;
  if (!ajaxUrl) return;

  event.preventDefault();

  const title = link.dataset.siblingTitle;

  navigateToSibling(link.href, ajaxUrl, title).catch((error) => {
    // Silently ignore aborted requests (user triggered another navigation)
    if (error instanceof DOMException && error.name === 'AbortError') return;
    // Fallback to full page navigation on error
    window.location.href = link.href;
  });
});

// ---------------------------------------------------------------------------
// Prefetch — hover
// ---------------------------------------------------------------------------

document.addEventListener(
  'pointerenter',
  (event) => {
    const link = event.target.closest('[data-sibling-link]');
    if (!link) return;
    if (link.hasAttribute('aria-current')) return;

    const ajaxUrl = link.dataset.ajaxUrl;
    if (ajaxUrl) prefetchSibling(ajaxUrl);
  },
  true
);

// ---------------------------------------------------------------------------
// Prefetch — visibility (IntersectionObserver)
// ---------------------------------------------------------------------------

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const link = /** @type {HTMLElement} */ (entry.target);
      const ajaxUrl = link.dataset.ajaxUrl;

      if (ajaxUrl && !link.hasAttribute('aria-current')) {
        prefetchSibling(ajaxUrl);
      }

      observer.unobserve(link);
    }
  },
  { rootMargin: '200px' }
);

/**
 * Observes all sibling links currently in the DOM for visibility-based prefetch
 */
const observeLinks = () => {
  const links = document.querySelectorAll('[data-sibling-link]:not([aria-current])');

  for (const link of links) {
    observer.observe(link);
  }
};

// Observe initial links and re-observe after navigation
observeLinks();
window.addEventListener('sibling:navigated', observeLinks);

// ---------------------------------------------------------------------------
// Browser history — back/forward
// ---------------------------------------------------------------------------

window.addEventListener('popstate', async (event) => {
  if (!event.state?.siblingNav || !event.state?.ajaxUrl) return;

  // Cancel any in-flight navigation
  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;

  document.body.classList.add('sibling-loading');
  const loadingText = document.querySelector('[data-loading-text]')?.dataset.loadingText || 'Loading product…';
  announce(loadingText);

  try {
    const html = await fetchPage(event.state.ajaxUrl, controller.signal);

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newContent = doc.querySelector('#MainContent') || doc.body;
    const currentContent = document.querySelector('#MainContent');

    if (!currentContent) return;

    const scrollY = window.scrollY;
    document.documentElement.style.scrollBehavior = 'auto';
    morph(currentContent, newContent, MORPH_OPTIONS);
    window.scrollTo(0, scrollY);
    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = '';
    });

    // Restore page title
    if (event.state.title) {
      document.title = event.state.title;
    }

    // Move focus to main content for screen readers
    const mainContent = document.querySelector('#MainContent');
    if (mainContent instanceof HTMLElement) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus({ preventScroll: true });
    }

    // Announce the navigation to screen readers
    announce(event.state.title || document.title);

    // Re-observe links in the new content
    observeLinks();
  } catch (error) {
    // Silently ignore aborted requests (user triggered another navigation)
    if (error instanceof DOMException && error.name === 'AbortError') return;
    // Fallback to full page reload on error
    window.location.reload();
  } finally {
    document.body.classList.remove('sibling-loading');
    if (activeController === controller) {
      activeController = null;
    }
  }
});
